import { FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from '@amzn/awsui-components-core/header';
import ContentLayout from '@amzn/awsui-components-core/content-layout';
import Alert from '@amzn/awsui-components-core/alert';
import Modal from '@amzn/awsui-components-core/modal';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import CampaignTable from 'src/components/organisms/CampaignTable';
import CancelCampaignModal from 'src/components/molecules/CancelCampaignModal';
import { useFetchCampaigns } from 'src/hooks/useListCampaigns';
import { useCancelCampaign } from 'src/hooks/useCancelCampaign';
import { useTranslate } from 'src/hooks/useTranslate';
import { buildRoute, VALUE_MATCHING_BASE } from 'src/routes/valueMatching/routes';
import { CampaignStatus } from 'src/@types/valueMatching/enums';
import { trackClick, PAGE_IDS } from 'src/utils/monitoring';

/** Shape of router state passed by ViewCampaignPage after a successful cancel. */
interface CancelSuccessLocationState {
    cancelSuccess?: { campaignId: string; version: number; title: string };
}

const LIST_CAMPAIGNS_BUNDLE = 'pages.listCampaigns';
const CANCEL_MODAL_BUNDLE = 'molecules.cancelCampaignModal';

/**
 * Statuses that allow Modify and Cancel actions from the list page.
 * only SCHEDULED, PROPAGATING (transitional), and ACTIVE
 * campaigns can be modified or cancelled. Aligned with ModifyCampaignPage's
 * MODIFIABLE_STATUSES and ViewCampaignPage's ACTIONABLE_STATUSES.
 */
const ACTIONABLE_STATUSES = new Set<string>([
    CampaignStatus.SCHEDULED,
    CampaignStatus.PROPAGATING,
    CampaignStatus.ACTIVE,
]);

/** Identity of the campaign being cancelled — owned by the page while the modal is open. */
interface CancelTarget {
    campaignId: string;
    version: number;
    title: string;
}

/** Auto-dismiss the post-cancel success banner after this many milliseconds. */
const SUCCESS_BANNER_TIMEOUT_MS = 5000;

const ListCampaignsPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignType: slug = '' } = useParams<{ campaignType: string }>();
  const { translate } = useTranslate(LIST_CAMPAIGNS_BUNDLE);
  const { translate: translateCancelModal } = useTranslate(CANCEL_MODAL_BUNDLE);

  const {
    campaigns,
    campaignType,
    campaignTypeName,
    loading,
    error,
    refetch,
    updateCampaignStatus,
  } = useFetchCampaigns(slug);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null);
  // Stores raw payload + originating slug so we can translate at render time
  // and auto-drop the banner when the user navigates to a different slug.
  const [successInfo, setSuccessInfo] = useState<{ slug: string; title: string } | null>(null);

  const {
    executeCancellation,
    cancelling,
    errorKey: cancelErrorKey,
    success: cancelSuccess,
    reset: resetCancel,
  } = useCancelCampaign();

  // Translate the cancel error at render time so a bundle that finishes
  // loading after the failure response still produces localized copy.
  const cancelError = cancelErrorKey ? translateCancelModal('error_message') : null;

  const createRoute = campaignType ? buildRoute.create(campaignType) : VALUE_MATCHING_BASE;

  const handleCampaignClick = useCallback(
    (id: string, version: string) => {
      if (!campaignType) return;
      trackClick('ViewCampaign', { page: PAGE_IDS.LIST_CAMPAIGNS, campaignType });
      navigate(buildRoute.view(campaignType, id, version));
    },
    [navigate, campaignType]
  );

  const handleCreateClick = useCallback(
    () => navigate(createRoute),
    [navigate, createRoute]
  );

  /**
   * Checks whether the given campaign status allows modify/cancel.
   * Status is passed directly from the selected table row — no array search needed.
   * Returns true if actionable, false (and shows modal) otherwise.
   */
  const isStatusActionable = useCallback(
    (status: string): boolean => {
      if (ACTIONABLE_STATUSES.has(status.toUpperCase())) return true;
      setShowStatusModal(true);
      return false;
    },
    []
  );

  const handleModifyClick = useCallback(
    (id: string, version: string, status: string) => {
      if (!campaignType) return;
      if (!isStatusActionable(status)) return;
      trackClick('ModifyCampaign', { page: PAGE_IDS.LIST_CAMPAIGNS, campaignType });
      navigate(buildRoute.modify(campaignType, id, version));
    },
    [navigate, campaignType, isStatusActionable]
  );

  const handleCancelClick = useCallback(
    (id: string, version: string, status: string, title: string) => {
      if (!isStatusActionable(status)) return;
      trackClick('CancelCampaignOpen', { page: PAGE_IDS.LIST_CAMPAIGNS, ...(campaignType && { campaignType }) });
      resetCancel();
      setCancelTarget({ campaignId: id, version: Number(version), title });
    },
    [isStatusActionable, resetCancel, campaignType]
  );

  const handleCancelDismiss = useCallback(() => {
    if (cancelling) return;
    trackClick('CancelCampaignDismiss', { page: PAGE_IDS.LIST_CAMPAIGNS, ...(campaignType && { campaignType }) });
    setCancelTarget(null);
    resetCancel();
  }, [cancelling, resetCancel, campaignType]);

  const handleCancelConfirm = useCallback(() => {
    if (!cancelTarget || !campaignType) return;
    trackClick('CancelCampaignConfirm', { page: PAGE_IDS.LIST_CAMPAIGNS, campaignType });
    executeCancellation(cancelTarget.campaignId, cancelTarget.version, campaignType);
  }, [cancelTarget, campaignType, executeCancellation]);

  // On a successful cancellation: close the modal, surface a success banner,
  // optimistically mark the row CANCELLED so the user sees the change
  // immediately, and refetch the list so the BFF authoritative state
  // overrides the local patch once it catches up.
  useEffect(() => {
    if (cancelSuccess && cancelTarget) {
      setSuccessInfo({ slug, title: cancelTarget.title });
      updateCampaignStatus(cancelTarget.campaignId, cancelTarget.version, CampaignStatus.CANCELLED);
      setCancelTarget(null);
      resetCancel();
      refetch();
    }
  }, [cancelSuccess, cancelTarget, resetCancel, refetch, updateCampaignStatus, slug]);

  // When the user is navigated here from the View page after a successful
  // cancel, the router carries a `cancelSuccess` payload in `location.state`.
  // Surface the same success banner and apply the optimistic status patch,
  // then strip the state so a refresh / back-nav does not re-trigger it.
  useEffect(() => {
    const state = location.state as CancelSuccessLocationState | null;
    const payload = state?.cancelSuccess;
    if (!payload) return;
    setSuccessInfo({ slug, title: payload.title });
    updateCampaignStatus(payload.campaignId, payload.version, CampaignStatus.CANCELLED);
    refetch();
    navigate(location.pathname, { replace: true });
  }, [location, navigate, refetch, updateCampaignStatus, slug]);

  // Banner is bound to the slug it was raised on — navigating to a different
  // campaign type makes it disappear automatically (no clear-effect needed,
  // which removes the batching race with the location-state effect on mount).
  // Translate at render time so the copy resolves once the i18n bundle loads.
  const successMessage = successInfo && successInfo.slug === slug
    ? translateCancelModal('success_message', { campaignTitle: successInfo.title })
    : null;

  // Auto-dismiss the success banner after a short delay.
  useEffect(() => {
    if (!successInfo) return;
    const timer = setTimeout(() => setSuccessInfo(null), SUCCESS_BANNER_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [successInfo]);

  return (
    <ContentLayout
      header={
        <Header variant="h1">
          {translate('manage_title', { campaignType: campaignTypeName })}
        </Header>
      }
    >
      <SpaceBetween size="s">
        {/* Live region so assistive tech announces the post-cancel banner.
            `aria-live="polite"` waits for the user's current announcement to
            finish; `role="status"` is the redundant-but-broadly-supported
            backup for older AT. The wrapper is render-stable so the announce
            actually fires when the message string toggles from null → set. */}
        <div role="status" aria-live="polite">
          {successMessage && (
            <Alert type="success" dismissible onDismiss={() => setSuccessInfo(null)}>
              {successMessage}
            </Alert>
          )}
        </div>

        {error && (
          <Alert type="error" header={translate('error_loading_campaigns')}>
            {error}
          </Alert>
        )}

        <CampaignTable
          campaigns={campaigns}
          campaignTitle={campaignTypeName}
          loading={loading}
          onCampaignClick={handleCampaignClick}
          onCreateClick={handleCreateClick}
          onModifyClick={handleModifyClick}
          onCancelClick={handleCancelClick}
        />
      </SpaceBetween>

      {/* Cancel campaign confirmation modal */}
      {cancelTarget && (
        <CancelCampaignModal
          visible
          onDismiss={handleCancelDismiss}
          onConfirm={handleCancelConfirm}
          campaignId={cancelTarget.campaignId}
          campaignTitle={cancelTarget.title}
          cancelling={cancelling}
          error={cancelError}
        />
      )}

      {/* Status validation modal — shown when user attempts modify/cancel on a non-actionable status */}
      <Modal
        visible={showStatusModal}
        onDismiss={() => setShowStatusModal(false)}
        closeAriaLabel="Close"
        header={translate('status_modal_header')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="primary" onClick={() => setShowStatusModal(false)}>
                {translate('status_modal_ok_button')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {translate('status_modal_message')}
      </Modal>
    </ContentLayout>
  );
};

export default ListCampaignsPage;
